import { useQueries } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const formatAddedTime = (addedtime) => {
  if (!addedtime) return "Invalid date";

  const now = new Date();
  const addedDate = new Date(addedtime);
  const diffTime = Math.abs(now.getTime() - addedDate.getTime());
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMinutes < 1) return "Added just now";
  if (diffMinutes === 1) return "Added 1 minute ago";
  if (diffMinutes < 60) return `Added ${diffMinutes} minutes ago`;
  if (diffHours === 1) return "Added 1 hour ago";
  if (diffHours < 24) return `Added ${diffHours} hours ago`;
  if (diffDays === 1) return "Added 1 day ago";
  if (diffDays < 7) return `Added ${diffDays} days ago`;
  if (diffWeeks === 1) return "Added 1 week ago";
  if (diffWeeks < 4) return `Added ${diffWeeks} weeks ago`;
  
  const month = addedDate.toLocaleString("default", { month: "long" });
  const day = addedDate.getDate();
  
  if (diffDays < 365) {
    return `Added ${month} ${day}`;
  }
  
  const year = addedDate.getFullYear();
  return `Added ${month} ${day}, ${year}`;
};

const fetchCardAndCompanyData = async ({ queryKey }) => {
  const [, companyId] = queryKey;
  
  if (!companyId) throw new Error('Company ID is required');

  const { data, error } = await supabase
    .from("cards")
    .select(`
      *,
      categories(name),
      company_overview(
        companyname,
        description,
        keyfeature1_title,
        keyfeature1_description,
        keyfeature2_title,
        keyfeature2_description,
        keyfeature3_title,
        keyfeature3_description,
        keyfeature4_title,
        keyfeature4_description,
        keyfeature5_title,
        keyfeature5_description,
        button_color,
        videopreview,
        videopreview_phone,
        companylink
      )
    `)
    .eq("id", companyId)
    .single();

  if (error) {
    throw new Error(`Error fetching company data: ${error.message}`);
  }

  if (!data) {
    throw new Error('Company not found');
  }

  return {
    ...data,
    company_overview: {
      ...data.company_overview,
      button_color: data.company_overview.button_color || "#7443E8"
    }
  };
};

const fetchFooterData = async () => {
  const { data, error } = await supabase
    .from("footercardcompany")
    .select("*")
    .single();

  if (error) {
    throw new Error(`Error fetching footer data: ${error.message}`);
  }

  if (!data) {
    throw new Error('Footer data not found');
  }

  return data;
};

export function useCardCompany(companyId) {
  const { handleError } = useQueryError();

  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.company.byId(companyId),
        queryFn: fetchCardAndCompanyData,
        enabled: Boolean(companyId),
        staleTime: 1000 * 60 * 5, // 5 minutos
        retry: (failureCount, error) => {
          // Solo reintenta errores de red, no errores de datos
          if (error.message.includes('not found')) return false;
          return failureCount < 3;
        },
        onError: (error) => {
          handleError({
            title: 'Error loading company data',
            message: error.message,
            severity: 'error'
          });
        }
      },
      {
        queryKey: queryKeys.footer.cardCompany,
        queryFn: fetchFooterData,
        staleTime: 1000 * 60 * 60, // 1 hora - el footer cambia con poca frecuencia
        retry: (failureCount, error) => {
          if (error.message.includes('not found')) return false;
          return failureCount < 2;
        },
        onError: (error) => {
          handleError({
            title: 'Error loading footer',
            message: error.message,
            severity: 'warning' // El footer no es crítico
          });
        }
      }
    ]
  });

  const [companyQuery, footerQuery] = results;

  // Procesar errores agregados
  const error = results.reduce((acc, query) => {
    if (query.error) {
      return acc ? new Error(`${acc.message}; ${query.error.message}`) : query.error;
    }
    return acc;
  }, null);

  // Si hay error en los datos de la compañía (crítico), propagarlo
  if (companyQuery.error) {
    throw companyQuery.error;
  }

  return {
    companyData: companyQuery.data,
    footerData: footerQuery.data,
    formattedAddedTime: companyQuery.data ? formatAddedTime(companyQuery.data.addedtime) : '',
    isLoading: results.some(query => query.isLoading),
    error,
    // Exponer estados adicionales para mejor control
    isFetching: results.some(query => query.isFetching),
    isRefetching: results.some(query => query.isRefetching)
  };
}